import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { FlaskConical, Loader2, X, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  computeJourneyAttribution,
  type ComputeScenarioOverrides,
  type JourneyConversionItem,
  type JourneyComputeResult,
  type ModelMetrics,
} from '../queries/attribution'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined) {
  if (n == null) return '—'
  return n.toLocaleString('en-IN')
}

function fmtPct(n: number | null | undefined) {
  if (n == null) return '—'
  return (n * 100).toFixed(1) + '%'
}

function fmtCurrency(n: number | null | undefined) {
  if (n == null || n === 0) return '—'
  return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 })
}

// ─── Model card ───────────────────────────────────────────────────────────────

function ModelCard({
  label,
  metrics,
  accent,
}: {
  label: string
  metrics: ModelMetrics
  accent?: boolean
}) {
  return (
    <div className={cn(
      'rounded-lg border p-3 flex-1 min-w-0',
      accent ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200 bg-white',
    )}>
      <p className={cn(
        'text-xs font-semibold uppercase tracking-wide mb-2',
        accent ? 'text-indigo-600' : 'text-gray-500',
      )}>{label}</p>
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Conversions</span>
          <span className="font-semibold text-gray-900">{fmt(metrics.conversions)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Unique customers</span>
          <span className="font-medium text-gray-700">{fmt(metrics.uniqueCustomers)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Revenue</span>
          <span className="font-medium text-emerald-700">{fmtCurrency(metrics.revenue)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-gray-500">Avg / conversion</span>
          <span className="font-medium text-gray-600">{fmtCurrency(metrics.avgRevenue)}</span>
        </div>
      </div>
    </div>
  )
}

// ─── Results ─────────────────────────────────────────────────────────────────

function SimulationResults({ result }: { result: JourneyComputeResult }) {
  const [breakdownOpen, setBreakdownOpen] = useState(false)
  const { scenario, funnel, models } = result
  // Campaign ids are time-ordered, so ascending id follows the order the journey's nodes were built. The name is
  // empty when the backend has none; show "Node N" (N = position in that order) with the id as the detail line.
  const campaignBreakdown = [...result.campaignBreakdown].sort((a, b) => a.campaignId - b.campaignId)

  return (
    <div className="mt-4 space-y-4">

      {/* Scenario banner */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
        <span className="text-gray-500">Configured:</span>
        <code className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-mono">
          {scenario.configuredConversionEvent ?? '—'}
        </code>
        <span className="text-gray-400">→</span>
        <span className="text-gray-500">Tested:</span>
        <code className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-mono">
          {scenario.testedConversionEvent}
        </code>
        {scenario.revenueField && (
          <>
            <span className="text-gray-300 hidden sm:inline">|</span>
            <span className="text-gray-500">Revenue field:</span>
            <code className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-mono">
              {scenario.revenueField}
            </code>
          </>
        )}
        <span className="text-gray-300 hidden sm:inline">|</span>
        <span className="text-gray-500">Filter:</span>
        <code className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-mono">
          {scenario.filter
            ? `${scenario.filter.attribute ?? scenario.filter.column} ${scenario.filter.logic ?? 'EQUALS'} ${scenario.filter.value ?? ''}`
            : 'none'}
        </code>
        <span className="text-gray-500">Counted per:</span>
        <code className="bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded font-mono">
          {scenario.idColumn ? `${scenario.idColumn} (each id)` : 'user'}
        </code>
        {scenario.idColumn && (
          <code className="bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded font-mono">
            {scenario.firstEventOnly ? 'first event only' : 'every event'}
          </code>
        )}
      </div>

      {/* Funnel summary. An older backend only returned users who converted as "users touched", which made the
          rate read 100%: when reach is not larger than the converters, say it is unavailable instead. */}
      {(() => {
        const reachKnown = funnel.totalUsersTouched > models.lastTouch.uniqueCustomers
        return (
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Users Reached', value: reachKnown ? fmt(funnel.totalUsersTouched) : '—',
                hint: reachKnown ? 'Distinct users the journey touched in the window' : 'Reach not reported by this backend version' },
              { label: 'Conversions', value: fmt(funnel.totalConversions),
                hint: 'Last-touch conversions (each id counts once when counted per id)' },
              { label: 'Conversion Rate', value: reachKnown ? fmtPct(funnel.conversionRate) : '—',
                hint: 'Converting users ÷ users reached' },
            ].map(({ label, value, hint }) => (
              <div key={label} title={hint} className="text-center border border-gray-200 rounded-lg py-2 px-3 bg-white">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-base font-bold text-gray-900 mt-0.5">{value}</p>
              </div>
            ))}
          </div>
        )
      })()}

      {/* 4 model cards */}
      <div className="flex gap-2">
        <ModelCard label="Last Touch"  metrics={models.lastTouch}  accent />
        <ModelCard label="First Touch" metrics={models.firstTouch} />
        <ModelCard label="Linear"      metrics={models.linear}      />
        <ModelCard label="Time Decay"  metrics={models.timeDecay}   />
      </div>

      {/* Campaign breakdown */}
      {campaignBreakdown.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setBreakdownOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-xs font-medium text-gray-600"
          >
            <span>Campaign Breakdown ({campaignBreakdown.length})</span>
            {breakdownOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {breakdownOpen && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-gray-500 uppercase tracking-wide">
                    <th className="py-2 pl-3 pr-4">Campaign</th>
                    <th className="py-2 pr-4 text-right">Touches</th>
                    <th className="py-2 pr-4 text-right">Last Touch</th>
                    <th className="py-2 pr-4 text-right">Linear</th>
                    <th className="py-2 pr-4 text-right">Time Decay</th>
                  </tr>
                </thead>
                <tbody>
                  {campaignBreakdown.map((c, i) => (
                    <tr key={c.campaignId} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 pl-3 pr-4">
                        <p className="font-medium text-gray-800">{c.campaignName ?? `Node ${i + 1}`}</p>
                        <p className="text-gray-400">#{c.campaignId}</p>
                      </td>
                      <td className="py-2 pr-4 text-right text-gray-600">{fmt(c.touchpoints)}</td>
                      <td className="py-2 pr-4 text-right">
                        <p className="font-medium text-gray-800">{fmt(c.lastTouch.conversions)}</p>
                        <p className="text-emerald-600">{fmtCurrency(c.lastTouch.revenue)}</p>
                      </td>
                      <td className="py-2 pr-4 text-right">
                        <p className="font-medium text-gray-800">{fmt(c.linear.conversions)}</p>
                        <p className="text-emerald-600">{fmtCurrency(c.linear.revenue)}</p>
                      </td>
                      <td className="py-2 pr-4 text-right">
                        <p className="font-medium text-gray-800">{fmt(c.timeDecay.conversions)}</p>
                        <p className="text-emerald-600">{fmtCurrency(c.timeDecay.revenue)}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

type Mode = 'default' | 'none' | 'custom'
const FILTER_LOGICS = ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'STARTS_WITH', 'ENDS_WITH', 'GREATER_THAN', 'LESS_THAN']

// ─── Main panel ───────────────────────────────────────────────────────────────

interface Props {
  item: JourneyConversionItem
  onClose: () => void
}

export function JourneySimulationPanel({ item, onClose }: Props) {
  const [eventName, setEventName]     = useState(item.conversionEvent)
  const [startDate, setStartDate]     = useState(item.windowStart)
  const [endDate, setEndDate]         = useState(item.windowEnd)
  const [revenueField, setRevenueField] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  // Scenario overrides. "default" sends nothing, so the journey's own configuration applies.
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [filterMode, setFilterMode]   = useState<Mode>('default')
  const [filterAttr, setFilterAttr]   = useState('')
  const [filterLogic, setFilterLogic] = useState('EQUALS')
  const [filterValue, setFilterValue] = useState('')
  const [idMode, setIdMode]           = useState<Mode>('default')
  const [idField, setIdField]         = useState('')
  const [firstEvent, setFirstEvent]   = useState<'default' | 'on' | 'off'>('default')

  function overrides(): ComputeScenarioOverrides {
    const o: ComputeScenarioOverrides = {}
    if (filterMode === 'none') o.filterAttribute = ''
    if (filterMode === 'custom') {
      o.filterAttribute = filterAttr.trim()
      o.filterLogic = filterLogic
      o.filterValue = filterValue
    }
    if (idMode === 'none') o.orderIdField = ''
    if (idMode === 'custom') o.orderIdField = idField.trim()
    if (firstEvent !== 'default') o.firstEventOnly = firstEvent === 'on'
    return o
  }

  const mutation = useMutation({
    mutationFn: () => computeJourneyAttribution({
      journeyId: item.journeyId,
      conversionEventName: eventName.trim(),
      startDate,
      endDate,
      revenueField: revenueField.trim() || undefined,
      ...overrides(),
    }),
  })

  function handleRun() {
    setValidationError(null)
    if (!eventName.trim()) {
      setValidationError('Conversion event name is required.')
      return
    }
    if (!startDate || !endDate) {
      setValidationError('Both start and end date are required.')
      return
    }
    if (startDate >= endDate) {
      setValidationError('Start date must be before end date.')
      return
    }
    if (filterMode === 'custom' && !filterAttr.trim()) {
      setValidationError('Enter the field to filter on, or choose "Journey default" / "None".')
      return
    }
    if (idMode === 'custom' && !idField.trim()) {
      setValidationError('Enter the id field, or choose "Journey default" / "None".')
      return
    }
    mutation.mutate()
  }

  return (
    <div className="p-4 bg-indigo-50/40 border-t border-indigo-100">
      {/* Panel header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-indigo-700">
          <FlaskConical className="h-4 w-4" />
          What-If Simulation
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close simulation panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Form */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1 min-w-[200px]">
          <label className="text-xs text-gray-500 font-medium">Conversion Event</label>
          <Input
            value={eventName}
            onChange={e => setEventName(e.target.value)}
            placeholder="e.g. add_to_cart"
            className="h-8 text-xs font-mono"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Start Date</label>
          <Input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">End Date</label>
          <Input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="flex flex-col gap-1 w-28">
          <label className="text-xs text-gray-500 font-medium">Revenue Field <span className="text-gray-400">(opt.)</span></label>
          <Input
            value={revenueField}
            onChange={e => setRevenueField(e.target.value)}
            placeholder="e.g. n1"
            className="h-8 text-xs font-mono"
          />
        </div>
        <Button
          size="sm"
          onClick={handleRun}
          disabled={mutation.isPending}
          className="h-8 text-xs"
        >
          {mutation.isPending
            ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Running…</>
            : <><FlaskConical className="h-3.5 w-3.5 mr-1.5" />Run</>}
        </Button>
      </div>

      {/* Scenario overrides */}
      <div className="mt-3">
        <button
          type="button"
          onClick={() => setAdvancedOpen(o => !o)}
          className="flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900"
        >
          {advancedOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          Scenario options
          <span className="font-normal text-gray-500">
            {filterMode === 'default' && idMode === 'default' && firstEvent === 'default'
              ? '— using the journey’s own configuration'
              : '— overriding the journey’s configuration'}
          </span>
        </button>
        {advancedOpen && (
          <div className="mt-2 grid gap-3 sm:grid-cols-3 border border-indigo-100 bg-white rounded-lg p-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">Conversion filter</label>
              <select
                value={filterMode}
                onChange={e => setFilterMode(e.target.value as Mode)}
                className="h-8 text-xs border border-gray-200 rounded-md px-2 bg-white"
              >
                <option value="default">Journey default</option>
                <option value="none">None (count all)</option>
                <option value="custom">Custom…</option>
              </select>
              {filterMode === 'custom' && (
                <div className="flex flex-col gap-1">
                  <Input value={filterAttr} onChange={e => setFilterAttr(e.target.value)}
                    placeholder="field, e.g. policy_type" className="h-8 text-xs font-mono" />
                  <select
                    value={filterLogic}
                    onChange={e => setFilterLogic(e.target.value)}
                    className="h-8 text-xs border border-gray-200 rounded-md px-2 bg-white"
                  >
                    {FILTER_LOGICS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <Input value={filterValue} onChange={e => setFilterValue(e.target.value)}
                    placeholder="value, e.g. Private Car" className="h-8 text-xs" />
                </div>
              )}
              <p className="text-[11px] text-gray-400">Which conversion events count, e.g. only Private Car policies.</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">Count conversions per</label>
              <select
                value={idMode}
                onChange={e => setIdMode(e.target.value as Mode)}
                className="h-8 text-xs border border-gray-200 rounded-md px-2 bg-white"
              >
                <option value="default">Journey default</option>
                <option value="none">User (one per user)</option>
                <option value="custom">Id field…</option>
              </select>
              {idMode === 'custom' && (
                <Input value={idField} onChange={e => setIdField(e.target.value)}
                  placeholder="e.g. policy_number" className="h-8 text-xs font-mono" />
              )}
              <p className="text-[11px] text-gray-400">An id (policy / order) makes each one its own conversion.</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">First event only</label>
              <select
                value={firstEvent}
                onChange={e => setFirstEvent(e.target.value as 'default' | 'on' | 'off')}
                disabled={idMode === 'none'}
                className="h-8 text-xs border border-gray-200 rounded-md px-2 bg-white disabled:opacity-50"
              >
                <option value="default">Journey default</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
              <p className="text-[11px] text-gray-400">Needs an id. Ignores repeat events for an id seen before the window.</p>
            </div>
          </div>
        )}
      </div>

      {/* Validation error */}
      {validationError && (
        <div className="mt-3 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          {validationError}
        </div>
      )}

      {/* API error */}
      {mutation.isError && (
        <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          {mutation.error instanceof Error ? mutation.error.message : 'Attribution compute failed.'}
        </div>
      )}

      {/* Results */}
      {mutation.isSuccess && <SimulationResults result={mutation.data} />}
    </div>
  )
}
